import { ROLES } from '@giftsay/constants';
import type { PaymentProvider } from '@giftsay/types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppSelector } from '../../../store/hooks.js';
import {
  getPaymentProviders,
  getStoreCredentials,
  listAuditLogs,
  removeStoreCredentials,
  saveStoreCredentials,
  type ListAuditLogsParams,
  type SaveStoreCredentialsPayload,
} from '../services/payment-admin-api.js';

export const paymentProvidersKey = ['payment-providers'] as const;
const storeCredentialsKey = (storeId: string) => ['payment-credentials', storeId] as const;

/** Platform admins only — the endpoint 403s for everyone else, so it isn't even asked. */
export function usePaymentProviders() {
  const role = useAppSelector((state) => state.auth.user?.role);
  return useQuery({
    queryKey: paymentProvidersKey,
    queryFn: getPaymentProviders,
    enabled: role === ROLES.SUPER_ADMIN || role === ROLES.ADMIN,
    staleTime: 5 * 60_000,
  });
}

export function useAuditLogs(params: ListAuditLogsParams) {
  return useQuery({
    queryKey: ['audit-logs', params],
    queryFn: () => listAuditLogs(params),
    placeholderData: keepPreviousData,
  });
}

export function useStoreCredentials(storeId: string) {
  return useQuery({
    queryKey: storeCredentialsKey(storeId),
    queryFn: () => getStoreCredentials(storeId),
    enabled: Boolean(storeId),
  });
}

/** Saving or removing keys also changes the store doc (hints) and the platform overview. */
function useCredentialsChanged(storeId: string) {
  const queryClient = useQueryClient();
  return (statuses: Awaited<ReturnType<typeof getStoreCredentials>>) => {
    queryClient.setQueryData(storeCredentialsKey(storeId), statuses);
    void queryClient.invalidateQueries({ queryKey: ['stores'] });
    void queryClient.invalidateQueries({ queryKey: paymentProvidersKey });
    void queryClient.invalidateQueries({ queryKey: ['settings', 'payment', storeId] });
  };
}

export function useSaveStoreCredentialsMutation(storeId: string) {
  const onChanged = useCredentialsChanged(storeId);
  return useMutation({
    mutationFn: (payload: SaveStoreCredentialsPayload) => saveStoreCredentials(storeId, payload),
    onSuccess: onChanged,
  });
}

export function useRemoveStoreCredentialsMutation(storeId: string) {
  const onChanged = useCredentialsChanged(storeId);
  return useMutation({
    mutationFn: (provider: PaymentProvider) => removeStoreCredentials(storeId, provider),
    onSuccess: onChanged,
  });
}
